import { toast } from 'sonner';
import { SIM_ERROR_INSUFFICIENT_FUNDS } from '@/lib/contracts';

function extractHumanReadableError(error: unknown): string {
  const raw = error instanceof Error ? error.message : String(error);
  const quoted = raw.match(/reason:\s*"([^"]+)"/i);
  if (quoted?.[1]) return quoted[1].trim();
  const exec = raw.match(/execution reverted[:(]\s*(.+?)(?:\n|$)/i);
  if (exec?.[1]) return exec[1].trim().slice(0, 300);
  try {
    const parsed = JSON.parse(raw) as { error?: { message?: string }; message?: string };
    const nested = parsed?.error?.message ?? parsed?.message;
    if (nested && typeof nested === 'string') return nested.slice(0, 300);
  } catch {
    /* not JSON */
  }
  return raw;
}

/**
 * Shared sim-error branch used by every tile drawer after a pre-flight
 * `simulateContractCall`.
 *
 * Before this helper existed each drawer swallowed the error with a
 * `console.error(...)` and silently returned — which meant that
 * `SIM_ERROR_INSUFFICIENT_FUNDS` (our sentinel for native balance
 * failures) never surfaced in the UI, and the classic "hit Send → nothing
 * happens → swap widget pops up" flow was dead.
 *
 * Callers pass:
 *   - `simError` — the string returned by `simulateContractCall`
 *     (`null` means success, the caller should skip this helper).
 *   - `context`  — short label (`'setPrice'`, `'buyout'`, …) used
 *     purely for logging.
 *   - `onInsufficientFunds` — fired when the sim error matches the
 *     insufficient-funds sentinel. Drawers hook this up to
 *     `setSwapOpen(true)` so the user can top up in-place.
 *
 * All other sim errors are surfaced through `sonner` as a destructive
 * toast so the user actually sees what went wrong (cooldown, anti-snipe,
 * not-holder, etc.). That copy is already human-readable because
 * `simulateContractCall` maps known revert names via `ERROR_MESSAGES`.
 */
export function handleSimError(
  simError: string,
  context: string,
  opts: { onInsufficientFunds: () => void },
): void {
  if (simError === SIM_ERROR_INSUFFICIENT_FUNDS) {
    opts.onInsufficientFunds();
    return;
  }
  console.error(`[drawer:${context}] simulation failed:`, simError);
  toast.error(simError);
}

const INSUFFICIENT_FUNDS_NEEDLES = [
  'insufficient funds',
  'exceeds balance',
  'insufficient balance for transfer',
  'exceeds allowance',
] as const;

const USER_REJECTION_NEEDLES = [
  'rejected',
  'denied',
  'cancelled',
  'canceled',
  'user refused',
  'transaction rejected',
] as const;

// MetaMask throws these when the keyring controller is in a state that can't
// service the request — most commonly because the vault auto-locked, or
// because an in-flight keyring mutation (e.g. EIP-7702 upgrade) collided
// with another operation. The executor in `use-transactions.tsx` catches
// these on the batch path and auto-falls-back to sequential signing; this
// list is the post-fallback safety net for cases where a sequential step
// also errors with the same class of message.
const WALLET_LOCKED_NEEDLES = [
  'keyringcontroller',
  'controller is locked',
  'vault is locked',
  'operation cannot be completed',
] as const;

/**
 * Shared handler for errors thrown out of `executeActions` (or any
 * wallet-submitted call). Matches insufficient-funds patterns from
 * common provider errors (MetaMask, Coinbase, Privy, ERC-5792 polling)
 * and triggers the swap modal in that case. User rejections are
 * swallowed silently — they're intentional.
 *
 * This is the post-submit twin of {@link handleSimError}: sim catches
 * insufficient funds *before* the wallet popup, this catches the cases
 * where pre-flight simulation was skipped (e.g. tile drawers that
 * don't pre-simulate) or where a race-condition balance refresh means
 * sim succeeded but the actual send still ran out of gas.
 */
/**
 * True when an error looks like an intentional user rejection (MetaMask
 * "user rejected" / "cancelled", etc.). Callers can use this to skip
 * surfacing an error banner for a deliberate cancel — the same set of
 * needles {@link handleTxError} swallows silently.
 */
export function isUserRejectionError(error: unknown): boolean {
  const msg = (error instanceof Error ? error.message : String(error)).toLowerCase();
  return USER_REJECTION_NEEDLES.some((n) => msg.includes(n));
}

export function handleTxError(
  error: unknown,
  context: string,
  opts?: { onInsufficientFunds?: () => void },
): void {
  const raw = error instanceof Error ? error.message : String(error);
  const msg = raw.toLowerCase();

  if (isUserRejectionError(error)) {
    console.info(`[drawer:${context}] user rejected transaction`);
    return;
  }

  if (WALLET_LOCKED_NEEDLES.some((n) => msg.includes(n))) {
    console.warn(`[drawer:${context}] wallet locked or keyring busy:`, raw);
    toast.error('Your wallet is locked. Open it, unlock, and try again.');
    return;
  }

  if (INSUFFICIENT_FUNDS_NEEDLES.some((n) => msg.includes(n))) {
    if (opts?.onInsufficientFunds) {
      opts.onInsufficientFunds();
      return;
    }
    toast.error('Insufficient funds for transaction');
    return;
  }

  console.error(`[drawer:${context}] transaction failed:`, error);
  const friendly = extractHumanReadableError(error);
  toast.error(friendly.length > 200 ? friendly.slice(0, 200) + '…' : friendly);
}
